import {
  Archive,
  Calendar,
  Check,
  ChevronRight,
  DollarSign,
  FileText,
  Mail,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Tag,
  Upload,
  User,
  Users,
  X,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import AddActivityModal from "../components/AddActivityModal"
import AddClientModal from "../components/AddClientModal"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import { cx } from "../lib/cx"
import type { Client, ClientActivity, Project, TimeEntry } from "../types"

type ClientsResponse = { clients?: Client[] }
type ProjectsResponse = { projects?: Project[] }
type TimeEntriesResponse = { timeEntries?: TimeEntry[] }
type NoteResponse = { ok?: boolean; note?: Client["notes"][number] }
type FileResponse = { file?: Client["files"][number] }
type ActivityResponse = { ok?: boolean; activity?: ClientActivity }
type ContactResponse = { ok?: boolean; contact?: Client["contacts"][number] }

const statusTone = (status: string): "success" | "warning" | "danger" | "neutral" => {
  if (status === "Active") return "success"
  if (status === "Lead") return "warning"
  if (status === "On Hold" || status === "Paused") return "danger"
  return "neutral"
}

const activityIcon = (type: ClientActivity["type"]) => {
  const icons = {
    call: Phone,
    email: Mail,
    file: FileText,
    invoice: DollarSign,
    task: Calendar,
    note: FileText,
    meeting: Users,
  }
  return icons[type] || FileText
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

const ClientsPage = () => {
  const [searchParams] = useSearchParams()
  const clientParam = searchParams.get("client")
  const [clients, setClients] = useState<Client[]>([])
  const [apiProjects, setApiProjects] = useState<Project[]>([])
  const [apiTimeEntries, setApiTimeEntries] = useState<TimeEntry[]>([])
  const [selectedId, setSelectedId] = useState(clientParam || "")
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    // Load from sessionStorage cache first (stale-while-revalidate)
    const cached = sessionStorage.getItem("bp_clients_cache")
    if (cached) {
      try {
        const { clients: cachedClients } = JSON.parse(cached)
        if (cachedClients?.length) {
          setClients(cachedClients)
          if (!selectedId && cachedClients.length > 0) {
            setSelectedId(clientParam || cachedClients[0].id)
          }
        }
      } catch { /* ignore bad cache */ }
    }

    const fetchData = async () => {
      try {
        const [clientsRes, projectsRes, timeRes] = await Promise.all([
          fetch("/api/clients"),
          fetch("/api/projects").catch(() => null),
          fetch("/api/time-entries").catch(() => null),
        ])

        if (clientsRes.ok) {
          const data = (await clientsRes.json()) as ClientsResponse
          if (data.clients && Array.isArray(data.clients)) {
            const validClients = data.clients.filter((c) => c && c.id && c.name)
            setClients(validClients)
            sessionStorage.setItem("bp_clients_cache", JSON.stringify({ clients: validClients }))
            if (!selectedId && validClients.length > 0) {
              setSelectedId(clientParam || validClients[0].id)
            }
          }
        }

        if (projectsRes?.ok) {
          const data = (await projectsRes.json()) as ProjectsResponse
          if (data.projects) setApiProjects(data.projects)
        }

        if (timeRes?.ok) {
          const data = (await timeRes.json()) as TimeEntriesResponse
          if (data.timeEntries) setApiTimeEntries(data.timeEntries)
        }
      } catch (error) {
        console.error("Failed to fetch data", error)
      }
    }
    fetchData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [statusFilter, setStatusFilter] = useState<string | null>("Active")
  const [activeTab, setActiveTab] = useState<"overview" | "activity" | "notes" | "files">(
    "overview",
  )
  const [newNote, setNewNote] = useState("")
  const [isAddClientOpen, setIsAddClientOpen] = useState(false)
  const [isAddActivityOpen, setIsAddActivityOpen] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [showAddContact, setShowAddContact] = useState(false)
  const [newContact, setNewContact] = useState({ name: "", role: "", email: "", phone: "" })
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isArchiving, setIsArchiving] = useState(false)

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(filteredClients.map((c) => c.id)))
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
    setSelectMode(false)
  }

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Archive ${selectedIds.size} client${selectedIds.size > 1 ? "s" : ""}? They'll be set to "Past" status.`)) return
    setIsArchiving(true)
    try {
      const res = await fetch("/api/clients-archive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientIds: Array.from(selectedIds) }),
      })
      if (res.ok) {
        setClients((prev) =>
          prev.map((c) => (selectedIds.has(c.id) ? { ...c, status: "Past" } : c)),
        )
        sessionStorage.removeItem("bp_clients_cache")
        clearSelection()
      } else {
        alert("Failed to archive clients. Please try again.")
      }
    } catch (e) {
      console.error("Bulk archive failed", e)
      alert("Failed to archive clients. Please try again.")
    }
    setIsArchiving(false)
  }

  // Handle client selection from URL params
  useEffect(() => {
    if (clientParam) {
      const client = clients.find((c) => c.id === clientParam)
      if (client) {
        setSelectedId(clientParam)
      }
    }
  }, [clientParam, clients])

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedId) ?? clients[0],
    [selectedId, clients],
  )

  const filteredClients = useMemo(() => {
    let filtered = clients

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (client) =>
          client?.name?.toLowerCase().includes(query) ||
          client?.company?.toLowerCase().includes(query) ||
          client?.email?.toLowerCase().includes(query) ||
          client?.tags?.some((tag) => tag.toLowerCase().includes(query)),
      )
    }

    if (statusFilter && statusFilter !== "All") {
      filtered = filtered.filter((client) => client.status === statusFilter)
    }

    return filtered
  }, [clients, searchQuery, statusFilter])

  // Get related data for selected client (from API data, matching by clientId or name)
  const clientProjects = useMemo(
    () =>
      selectedClient
        ? apiProjects.filter(
            (p) => p.clientId === selectedClient.id || p.client === selectedClient.name,
          )
        : [],
    [selectedClient, apiProjects],
  )

  const clientTimeEntries = useMemo(
    () =>
      selectedClient
        ? apiTimeEntries.filter(
            (te) => te.client === selectedClient.id || te.client === selectedClient.name,
          )
        : [],
    [selectedClient, apiTimeEntries],
  )

  const totalHoursLogged = useMemo(
    () => clientTimeEntries.reduce((sum, te) => sum + te.durationHours, 0),
    [clientTimeEntries],
  )

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClient) return
    try {
      const res = await fetch(`/api/clients/${selectedClient.id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newNote }),
      })
      const data = (await res.json()) as NoteResponse
      if (data.ok && data.note) {
        const createdNote = data.note
        setClients((prev) =>
          prev.map((c) =>
            c.id === selectedClient.id
              ? { ...c, notes: [createdNote, ...c.notes] }
              : c
          )
        )
      }
      setNewNote("")
    } catch (e) {
      console.error("Failed to add note", e)
    }
  }

  const [uploadCategory, setUploadCategory] = useState("other")
  const [isUploading, setIsUploading] = useState(false)
  const [fileCategoryFilter, setFileCategoryFilter] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [editingFileId, setEditingFileId] = useState<string | null>(null)
  const [editFileName, setEditFileName] = useState("")
  const [editFileCategory, setEditFileCategory] = useState("")

  const fileCategories = [
    { value: "contract", label: "Contracts" },
    { value: "brand", label: "Brand Assets" },
    { value: "receipt", label: "Receipts" },
    { value: "screenshot", label: "Screenshots" },
    { value: "proposal", label: "Proposals" },
    { value: "other", label: "Other" },
  ]

  const uploadFiles = async (files: File[]) => {
    if (!selectedClient || files.length === 0) return
    setIsUploading(true)

    for (const file of files) {
      const form = new FormData()
      form.append("file", file)
      form.append("category", uploadCategory)

      try {
        const res = await fetch(`/api/clients/${selectedClient.id}/files`, {
          method: "POST",
          body: form,
        })
        if (res.ok) {
          const data = (await res.json()) as FileResponse
          if (data.file) {
            const uploadedFile = data.file
            setClients((prev) =>
              prev.map((c) =>
                c.id === selectedClient.id
                  ? { ...c, files: [uploadedFile, ...c.files] }
                  : c,
              ),
            )
          }
        }
      } catch (e) {
        console.error("Upload failed", e)
      }
    }
    sessionStorage.removeItem("bp_clients_cache")
    setIsUploading(false)
  }

  const handleFileUpload = () => {
    if (!selectedClient) return
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.onchange = () => {
      if (input.files) uploadFiles(Array.from(input.files))
    }
    input.click()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files) uploadFiles(Array.from(e.dataTransfer.files))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const startEditingFile = (file: { id: string; name: string; category: string }) => {
    setEditingFileId(file.id)
    setEditFileName(file.name)
    setEditFileCategory(file.category || "other")
  }

  const handleFileSave = async () => {
    if (!editingFileId || !selectedClient) return
    const file = selectedClient.files.find((f) => f.id === editingFileId)
    if (!file) return

    const nameChanged = editFileName.trim() && editFileName.trim() !== file.name
    const categoryChanged = editFileCategory !== (file.category || "other")
    if (!nameChanged && !categoryChanged) { setEditingFileId(null); return }

    const body: { name?: string; category?: string } = {}
    if (nameChanged) body.name = editFileName.trim()
    if (categoryChanged) body.category = editFileCategory

    setClients((prev) =>
      prev.map((c) =>
        c.id === selectedClient.id
          ? { ...c, files: c.files.map((f) => f.id === editingFileId ? { ...f, ...body } : f) }
          : c,
      ),
    )
    setEditingFileId(null)

    try {
      await fetch(`/api/files/${editingFileId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      sessionStorage.removeItem("bp_clients_cache")
    } catch { /* ok */ }
  }

  const handleFileReplace = (fileId: string) => {
    if (!selectedClient) return
    const input = document.createElement("input")
    input.type = "file"
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      setIsUploading(true)
      const form = new FormData()
      form.append("file", file)
      try {
        const res = await fetch(`/api/files/${fileId}`, {
          method: "PUT",
          body: form,
        })
        if (res.ok) {
          const data = (await res.json()) as FileResponse
          if (data.file) {
            const replacedFile = data.file
            setClients((prev) =>
              prev.map((c) =>
                c.id === selectedClient.id
                  ? { ...c, files: c.files.map((f) => f.id === fileId ? { ...f, ...replacedFile } : f) }
                  : c,
              ),
            )
            sessionStorage.removeItem("bp_clients_cache")
          }
        }
      } catch (e) {
        console.error("Replace failed", e)
      }
      setIsUploading(false)
    }
    input.click()
  }

  const handleFileDelete = async (fileId: string) => {
    if (!selectedClient) return
    if (!confirm("Delete this file?")) return
    setClients((prev) =>
      prev.map((c) =>
        c.id === selectedClient.id
          ? { ...c, files: c.files.filter((f) => f.id !== fileId) }
          : c,
      ),
    )
    try {
      await fetch(`/api/files/${fileId}`, { method: "DELETE" })
      sessionStorage.removeItem("bp_clients_cache")
    } catch { /* ok */ }
  }

  const handleAddClient = async (clientData: Omit<Client, "id" | "createdAt" | "lastContacted">) => {
    if (editingClient) {
      // Update existing client
      try {
        const res = await fetch(`/api/clients/${editingClient.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(clientData),
        })

        if (!res.ok) throw new Error("Failed to update client")

        setClients((prev) =>
          prev.map((c) =>
            c.id === editingClient.id
              ? {
                  ...c,
                  ...clientData,
                }
              : c,
          ),
        )
        sessionStorage.removeItem("bp_clients_cache")
        setEditingClient(null)
      } catch (error) {
        console.error("Failed to update client:", error)
        alert("Failed to update client. Please try again.")
      }
    } else {
      // Create new client
      try {
        const res = await fetch("/api/clients", {
          method: "POST",
          body: JSON.stringify(clientData),
        })

        if (!res.ok) throw new Error("Failed to create client")

        const { id } = await res.json()
        const newClient: Client = {
          ...clientData,
          id,
          createdAt: new Date().toISOString(),
        }
        setClients((prev) => [...prev, newClient])
        sessionStorage.removeItem("bp_clients_cache")
        setSelectedId(newClient.id)
      } catch (error) {
        console.error("Failed to create client:", error)
        alert("Failed to create client. Please try again.")
      }
    }
    setIsAddClientOpen(false)
  }

  const handleDeleteClient = async (clientId: string) => {
    try {
      const res = await fetch(`/api/clients/${clientId}`, {
        method: "DELETE",
      })

      if (!res.ok) {
        throw new Error("Failed to delete client")
      }

      setClients((prev) => prev.filter((c) => c.id !== clientId))
      sessionStorage.removeItem("bp_clients_cache")
      if (selectedId === clientId) {
        const remaining = clients.filter((c) => c.id !== clientId)
        setSelectedId(remaining[0]?.id || "")
      }
    } catch (error) {
      console.error("Failed to delete client:", error)
      alert("Failed to delete client. Please try again.")
    }
  }

  const handleEditClient = () => {
    if (selectedClient) {
      setEditingClient(selectedClient)
      setIsAddClientOpen(true)
    }
  }

  const handleAddActivity = async (activityData: Omit<ClientActivity, "id" | "date">) => {
    if (!selectedClient) return

    try {
      const res = await fetch(`/api/clients/${selectedClient.id}/activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityData),
      })
      const data = (await res.json()) as ActivityResponse

      const newActivity: ClientActivity = data.ok && data.activity
        ? data.activity
        : { ...activityData, id: `act-${Date.now()}`, date: new Date().toISOString() }

      setClients((prev) =>
        prev.map((client) =>
          client.id === selectedId
            ? {
                ...client,
                activity: [newActivity, ...client.activity],
                lastContacted: newActivity.date,
              }
            : client,
        ),
      )
    } catch {
      // Fallback to local-only
      const newActivity: ClientActivity = {
        ...activityData,
        id: `act-${Date.now()}`,
        date: new Date().toISOString(),
      }
      setClients((prev) =>
        prev.map((client) =>
          client.id === selectedId
            ? { ...client, activity: [newActivity, ...client.activity] }
            : client,
        ),
      )
    }

    setIsAddActivityOpen(false)
    setActiveTab("activity")
  }

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left sidebar - Client list */}
      <Card className="col-span-12 xl:col-span-4">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-slate-900">Clients</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setSelectMode(!selectMode); if (selectMode) clearSelection() }}
              className={cx(
                "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                selectMode
                  ? "bg-slate-200 text-slate-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200",
              )}
            >
              {selectMode ? "Cancel" : "Select"}
            </button>
            <button
              type="button"
              onClick={() => setIsAddClientOpen(true)}
              className="flex items-center gap-1.5 rounded-full bg-moss px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-moss/90"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Client
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search clients..."
              className="w-full rounded-full border border-line bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-moss"
            />
          </div>
        </div>

        {/* Status filters */}
        <div className="mt-3 flex flex-wrap gap-2">
          {["All", "Active", "Lead", "Paused", "Past"].map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status === "All" ? null : status)}
              className={cx(
                "rounded-full px-3 py-1 text-xs font-semibold transition",
                statusFilter === (status === "All" ? null : status)
                  ? "bg-moss text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200",
              )}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Select all / archive bar */}
        {selectMode && (
          <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectedIds.size === filteredClients.length ? () => setSelectedIds(new Set()) : selectAll}
                className="text-xs font-medium text-moss hover:underline"
              >
                {selectedIds.size === filteredClients.length ? "Deselect All" : "Select All"}
              </button>
              <span className="text-xs text-stone">
                {selectedIds.size} selected
              </span>
            </div>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleBulkArchive}
                disabled={isArchiving}
                className="flex items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                <Archive className="h-3.5 w-3.5" />
                {isArchiving ? "Archiving..." : `Archive ${selectedIds.size}`}
              </button>
            )}
          </div>
        )}

        {/* Client list */}
        <div className="mt-5 space-y-2">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className={cx(
                "flex w-full items-start gap-2 rounded-2xl border border-line px-4 py-3 text-left text-sm transition",
                !selectMode && selectedId === client.id
                  ? "border-moss bg-moss/5"
                  : "bg-white hover:bg-slate-50",
                selectMode && selectedIds.has(client.id) && "border-moss bg-moss/5",
              )}
            >
              {selectMode && (
                <button
                  type="button"
                  onClick={() => toggleSelect(client.id)}
                  className={cx(
                    "mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition",
                    selectedIds.has(client.id)
                      ? "border-moss bg-moss text-white"
                      : "border-line hover:border-moss",
                  )}
                >
                  {selectedIds.has(client.id) && (
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (selectMode) {
                    toggleSelect(client.id)
                  } else {
                    setSelectedId(client.id)
                    setActiveTab("overview")
                  }
                }}
                className="flex-1 text-left"
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-900">{client.name}</p>
                  <StatusPill tone={statusTone(client.status)} label={client.status} />
                </div>
                <p className="mt-1 text-xs text-stone">{client.company}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {client.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                  {client.tags.length > 2 && (
                    <span className="text-xs text-stone">+{client.tags.length - 2}</span>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Right panel - Client details */}
      <div className="col-span-12 xl:col-span-8 space-y-6">
        {selectedClient && (
          <>
            {/* Header */}
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-semibold text-slate-900">{selectedClient.name}</p>
                    <StatusPill tone={statusTone(selectedClient.status)} label={selectedClient.status} />
                  </div>
                  <p className="mt-1 text-sm text-stone">{selectedClient.company}</p>
                  {selectedClient.website && (
                    <a
                      href={selectedClient.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-moss hover:underline"
                    >
                      {selectedClient.website.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={handleEditClient}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-slate-600 hover:bg-slate-50"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Quick stats */}
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-stone">Lifetime Value</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedClient.lifetimeValue}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-stone">Monthly Recurring</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedClient.monthlyRecurring}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-stone">Outstanding</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedClient.outstandingBalance}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-stone">Hours Logged</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{totalHoursLogged}</p>
                </div>
              </div>
            </Card>

            {/* Tabs */}
            <Card>
              <div className="flex items-center gap-1 border-b border-line">
                {[
                  { id: "overview", label: "Overview" },
                  { id: "activity", label: "Activity" },
                  { id: "notes", label: "Notes" },
                  { id: "files", label: "Files" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    className={cx(
                      "border-b-2 px-4 py-3 text-sm font-medium transition",
                      activeTab === tab.id
                        ? "border-moss text-moss"
                        : "border-transparent text-stone hover:text-slate-700",
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="mt-6">
                {activeTab === "overview" && (
                  <div className="space-y-6">
                    {/* Contacts */}
                    <div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">Contacts</p>
                        <button
                          type="button"
                          onClick={() => setShowAddContact(!showAddContact)}
                          className="flex items-center gap-1 text-xs text-moss hover:underline"
                        >
                          <Plus className="h-3 w-3" />
                          Add Contact
                        </button>
                      </div>
                      {showAddContact && (
                        <div className="mt-3 rounded-xl border border-line bg-slate-50 p-3 space-y-2">
                          <input
                            type="text"
                            placeholder="Name *"
                            value={newContact.name}
                            onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Role"
                              value={newContact.role}
                              onChange={(e) => setNewContact(prev => ({ ...prev, role: e.target.value }))}
                              className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
                            />
                            <input
                              type="email"
                              placeholder="Email"
                              value={newContact.email}
                              onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                              className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="tel"
                              placeholder="Phone"
                              value={newContact.phone}
                              onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                              className="flex-1 rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!newContact.name.trim() || !selectedClient) return
                                try {
                                  const res = await fetch(`/api/clients/${selectedClient.id}/contacts`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify(newContact),
                                  })
                                  const data = (await res.json()) as ContactResponse
                                  if (data.ok && data.contact) {
                                    const createdContact = data.contact
                                    setClients(prev => prev.map(c =>
                                      c.id === selectedClient.id
                                        ? { ...c, contacts: [...c.contacts, createdContact] }
                                        : c
                                    ))
                                  }
                                } catch { /* ignore */ }
                                setNewContact({ name: "", role: "", email: "", phone: "" })
                                setShowAddContact(false)
                              }}
                              className="rounded-lg bg-moss px-3 py-1.5 text-xs font-medium text-white hover:bg-moss/90"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => { setShowAddContact(false); setNewContact({ name: "", role: "", email: "", phone: "" }) }}
                              className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="mt-3 space-y-2">
                        {selectedClient.contacts.map((contact) => (
                          <div
                            key={contact.id}
                            className="flex items-center justify-between rounded-xl border border-line bg-white p-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-moss/10 text-moss">
                                <User className="h-5 w-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {contact.name}
                                  </p>
                                  {contact.isPrimary && (
                                    <span className="rounded-full bg-moss/10 px-2 py-0.5 text-xs font-medium text-moss">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-stone">{contact.role}</p>
                                <div className="mt-1 flex items-center gap-3 text-xs text-stone">
                                  <a href={`mailto:${contact.email}`} className="hover:text-moss">
                                    {contact.email}
                                  </a>
                                  <a href={`tel:${contact.phone}`} className="hover:text-moss">
                                    {contact.phone}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Financials */}
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Financials</p>
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        <div className="rounded-xl border border-line bg-white p-4">
                          <p className="text-xs text-stone">Total Invoiced</p>
                          <p className="mt-1 text-lg font-semibold text-slate-900">
                            {selectedClient.totalInvoiced}
                          </p>
                        </div>
                        <div className="rounded-xl border border-line bg-white p-4">
                          <p className="text-xs text-stone">Total Paid</p>
                          <p className="mt-1 text-lg font-semibold text-slate-900">
                            {selectedClient.totalPaid}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Linked Projects */}
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Linked Projects</p>
                      <div className="mt-3 space-y-2">
                        {clientProjects.length > 0 ? (
                          clientProjects.map((project) => (
                            <Link
                              key={project.id}
                              to={`/projects/${project.id}`}
                              className="flex items-center justify-between rounded-xl border border-line bg-white p-3 transition hover:bg-slate-50"
                            >
                              <div>
                                <p className="text-sm font-semibold text-slate-900">
                                  {project.name}
                                </p>
                                <p className="text-xs text-stone">
                                  {project.tasksDone}/{project.tasksTotal} tasks ·{" "}
                                  {project.hoursUsed}/{project.budgetHours} hours
                                </p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-stone" />
                            </Link>
                          ))
                        ) : (
                          <p className="text-sm text-stone">No projects linked</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "activity" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-900">Activity Timeline</p>
                      <button
                        type="button"
                        onClick={() => setIsAddActivityOpen(true)}
                        className="flex items-center gap-1.5 rounded-lg bg-moss px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-moss/90"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Activity
                      </button>
                    </div>
                    {selectedClient.activity.length > 0 ? (
                      selectedClient.activity.map((activity) => {
                        const Icon = activityIcon(activity.type)
                        return (
                          <div
                            key={activity.id}
                            className="flex items-start gap-3 rounded-xl border border-line bg-white p-4"
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-slate-900 capitalize">
                                  {activity.type}
                                </p>
                                {activity.author && (
                                  <span className="text-xs text-stone">by {activity.author}</span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-slate-700">{activity.summary}</p>
                              {activity.metadata && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {Object.entries(activity.metadata).map(([key, value]) => (
                                    <span
                                      key={key}
                                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                                    >
                                      {key}: {String(value)}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <p className="mt-2 text-xs text-stone">{formatDate(activity.date)}</p>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <p className="py-8 text-center text-sm text-stone">No activity recorded</p>
                    )}
                  </div>
                )}

                {activeTab === "notes" && (
                  <div className="space-y-4">
                    {/* Add note */}
                    <div className="rounded-xl border border-line bg-white p-4">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note about this client..."
                        className="w-full resize-none rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-moss"
                        rows={3}
                      />
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setNewNote("")}
                          className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddNote}
                          className="rounded-lg bg-moss px-3 py-1.5 text-xs font-medium text-white hover:bg-moss/90"
                        >
                          Save Note
                        </button>
                      </div>
                    </div>

                    {/* Notes list */}
                    <div className="space-y-3">
                      {selectedClient.notes.length > 0 ? (
                        selectedClient.notes.map((note) => (
                          <div
                            key={note.id}
                            className="rounded-xl border border-line bg-white p-4"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm text-slate-900">{note.content}</p>
                                <div className="mt-2 flex items-center gap-2 text-xs text-stone">
                                  <span>{note.author}</span>
                                  <span>·</span>
                                  <span>{formatDate(note.createdAt)}</span>
                                  {note.type !== "note" && (
                                    <>
                                      <span>·</span>
                                      <span className="capitalize">{note.type}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="py-8 text-center text-sm text-stone">No notes yet</p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "files" && (
                  <div className="space-y-4">
                    {/* Dropzone */}
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={handleFileUpload}
                      className={cx(
                        "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition",
                        isDragging
                          ? "border-moss bg-moss/5"
                          : "border-line bg-slate-50 hover:border-moss hover:bg-slate-100/50",
                      )}
                    >
                      <Upload className={cx("mx-auto h-8 w-8", isDragging ? "text-moss" : "text-stone")} />
                      <p className="mt-3 text-sm font-medium text-slate-700">
                        {isUploading ? "Uploading..." : isDragging ? "Drop files here" : "Drag & drop files here, or click to browse"}
                      </p>
                      <p className="mt-1 text-xs text-stone">
                        PDF, DOC, images, any file up to 100MB
                      </p>
                      <div className="mt-4 flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs text-stone">Category:</span>
                        <select
                          value={uploadCategory}
                          onChange={(e) => setUploadCategory(e.target.value)}
                          className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-moss"
                        >
                          {fileCategories.map((cat) => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Category filter */}
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setFileCategoryFilter(null)}
                        className={cx(
                          "rounded-full px-3 py-1 text-xs font-medium transition",
                          fileCategoryFilter === null
                            ? "bg-moss text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                        )}
                      >
                        All ({selectedClient.files.length})
                      </button>
                      {fileCategories.map((cat) => {
                        const count = selectedClient.files.filter((f) => (f.category || "other") === cat.value).length
                        if (count === 0) return null
                        return (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => setFileCategoryFilter(cat.value)}
                            className={cx(
                              "rounded-full px-3 py-1 text-xs font-medium transition",
                              fileCategoryFilter === cat.value
                                ? "bg-moss text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                            )}
                          >
                            {cat.label} ({count})
                          </button>
                        )
                      })}
                    </div>

                    {/* Files list */}
                    <div className="space-y-2">
                      {(() => {
                        const filtered = fileCategoryFilter
                          ? selectedClient.files.filter((f) => (f.category || "other") === fileCategoryFilter)
                          : selectedClient.files
                        return filtered.length > 0 ? (
                          filtered.map((file) => {
                            const catLabel = fileCategories.find((c) => c.value === (file.category || "other"))?.label || "Other"
                            const isEditing = editingFileId === file.id
                            return (
                              <div
                                key={file.id}
                                className="rounded-xl border border-line bg-white p-3"
                              >
                                {isEditing ? (
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                                      <FileText className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div className="flex flex-1 flex-wrap items-center gap-2">
                                      <input
                                        type="text"
                                        value={editFileName}
                                        onChange={(e) => setEditFileName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleFileSave(); if (e.key === "Escape") setEditingFileId(null) }}
                                        className="flex-1 min-w-[150px] rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
                                        autoFocus
                                      />
                                      <select
                                        value={editFileCategory}
                                        onChange={(e) => setEditFileCategory(e.target.value)}
                                        className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-moss"
                                      >
                                        {fileCategories.map((cat) => (
                                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                      </select>
                                      <button type="button" onClick={handleFileSave} className="flex h-8 w-8 items-center justify-center rounded-lg bg-moss text-white hover:bg-moss/90">
                                        <Check className="h-4 w-4" />
                                      </button>
                                      <button type="button" onClick={() => setEditingFileId(null)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-600 hover:bg-slate-50">
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                                        <FileText className="h-5 w-5 text-slate-600" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-slate-900">{file.name}</p>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-stone">
                                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                                            {catLabel}
                                          </span>
                                          <span>{file.size}</span>
                                          <span>·</span>
                                          <span>{file.uploadedBy}</span>
                                          <span>·</span>
                                          <span>{formatDate(file.uploadedAt)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <a
                                        href={`/api/files/${file.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                                      >
                                        Open
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => startEditingFile(file)}
                                        title="Rename / recategorize"
                                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleFileReplace(file.id)}
                                        title="Replace file"
                                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                      >
                                        <RefreshCw className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleFileDelete(file.id)}
                                        title="Delete file"
                                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })
                        ) : (
                          <p className="py-8 text-center text-sm text-stone">
                            {fileCategoryFilter ? "No files in this category" : "No files uploaded"}
                          </p>
                        )
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Modals */}
      <AddClientModal
        isOpen={isAddClientOpen}
        onClose={() => {
          setIsAddClientOpen(false)
          setEditingClient(null)
        }}
        onSave={handleAddClient}
        client={editingClient}
        onDelete={handleDeleteClient}
      />
      {selectedClient && (
        <AddActivityModal
          isOpen={isAddActivityOpen}
          onClose={() => setIsAddActivityOpen(false)}
          onSave={handleAddActivity}
          clientName={selectedClient.name}
        />
      )}
    </div>
  )
}

export default ClientsPage
